import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Event {
  id: string;
  title: string;
  description: string | null;
  type: string;
  location: string | null;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  registrationEnabled: boolean;
  registrationLimit: number | null;
  registrationCount: number;
  price: number | null;
  isPublic: boolean;
}

export function EventsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['events', selectedType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedType) params.set('type', selectedType);
      params.set('from', new Date().toISOString());
      const response = await api.get<Event[]>(`/events?${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (eventId: string) => {
      const response = await api.post(`/events/${eventId}/register`, {
        memberId: user?.memberId,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const typeLabels: Record<string, string> = {
    service: 'Services',
    class: 'Classes',
    social: 'Social',
    lifecycle: 'Lifecycle',
    holiday: 'Holiday',
    general: 'General',
  };

  const typeColors: Record<string, string> = {
    service: 'bg-blue-100 text-blue-800',
    class: 'bg-purple-100 text-purple-800',
    social: 'bg-green-100 text-green-800',
    lifecycle: 'bg-pink-100 text-pink-800',
    holiday: 'bg-amber-100 text-amber-800',
    general: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Upcoming Events</h1>
          <p className="mt-2 text-gray-600">Browse and register for synagogue events</p>
        </div>

        {/* Type Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedType('')}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
              !selectedType
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border hover:bg-gray-50'
            }`}
          >
            All Events
          </button>
          {Object.entries(typeLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSelectedType(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${
                selectedType === key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 border hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Events List */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : data?.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No upcoming events</h3>
            <p className="mt-2 text-gray-500">Check back later for new events.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data?.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[event.type] || typeColors.general}`}>
                          {typeLabels[event.type] || event.type}
                        </span>
                        {event.price && event.price > 0 && (
                          <span className="text-sm text-gray-500">
                            ${event.price}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {event.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(event.startTime)}
                        </span>
                        {!event.allDay && (
                          <span className="flex items-center gap-1">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatTime(event.startTime)}
                            {event.endTime && ` - ${formatTime(event.endTime)}`}
                          </span>
                        )}
                        {event.location && (
                          <span className="flex items-center gap-1">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {event.location}
                          </span>
                        )}
                      </div>
                      {event.description && (
                        <p className="text-gray-600 line-clamp-2">{event.description}</p>
                      )}
                    </div>

                    {event.registrationEnabled && (
                      <div className="ml-6 flex-shrink-0">
                        {event.registrationLimit && (
                          <p className="text-sm text-gray-500 mb-2 text-right">
                            {event.registrationLimit - event.registrationCount} spots left
                          </p>
                        )}
                        <button
                          onClick={() => registerMutation.mutate(event.id)}
                          disabled={registerMutation.isPending}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                        >
                          {registerMutation.isPending ? 'Registering...' : 'Register'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
